/**
 * Conflito de interesse no vínculo conselheiro × empresa (etapa 12, decisões
 * do PM de 05/10/2026). Regras puras; quem chama consulta o banco.
 *
 * Há conflito quando:
 * - CARTEIRA: o conselheiro já atende uma empresa que é concorrente da nova
 *   (está na lista de concorrentes de uma, ou a outra está na da nova);
 * - VINCULO_DECLARADO: ele declarou ser ou ter sido, nos últimos 2 anos, sócio,
 *   administrador, empregado ou consultor de um concorrente da nova.
 *
 * O vínculo com conflito é recusado; a rede pode liberar com justificativa.
 */
import { normalizarCnpj } from './conselheiro.js';

export const RELACOES_DECLARADAS = ['SOCIO', 'ADMINISTRADOR', 'EMPREGADO', 'CONSULTOR'] as const;
export type RelacaoDeclarada = (typeof RELACOES_DECLARADAS)[number];
export const NOME_DA_RELACAO: Record<RelacaoDeclarada, string> = {
  SOCIO: 'sócio',
  ADMINISTRADOR: 'administrador',
  EMPREGADO: 'empregado',
  CONSULTOR: 'consultor',
};

/** Vínculo encerrado há mais que isto não conta. */
export const ANOS_DO_VINCULO_DECLARADO = 2;

/** Mínimo de texto para a rede liberar um vínculo com conflito. */
export const MINIMO_DA_JUSTIFICATIVA = 20;

export type Identidade = { nome: string; cnpj?: string | null; hackos_empresa_id?: string | null };

const SUFIXOS = new Set(['ltda', 'sa', 'me', 'epp', 'eireli', 'mei', 'ss', 'cia', 'limitada', 's']);

/** "Padaria São João Ltda." → "padaria sao joao". */
export function normalizarNomeDeEmpresa(nome: string): string {
  const palavras = nome
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\bs\s*[./]\s*a\b\.?/g, ' sa ')
    .replace(/[^a-z0-9 ]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
  while (palavras.length > 1 && SUFIXOS.has(palavras[palavras.length - 1])) palavras.pop();
  return palavras.join(' ');
}

/** Raiz do CNPJ (8 primeiros dígitos): matriz e filiais são a mesma empresa. */
function raiz(cnpj: string | null | undefined): string | null {
  return normalizarCnpj(cnpj)?.slice(0, 8) ?? null;
}

/**
 * É a mesma empresa? Com CNPJ dos dois lados, decide o CNPJ (pela raiz). Sem
 * ele, o mesmo id no CRM, ou o mesmo nome depois de normalizado.
 */
export function mesmaEmpresa(a: Identidade, b: Identidade): boolean {
  const ra = raiz(a.cnpj);
  const rb = raiz(b.cnpj);
  if (ra && rb) return ra === rb;
  if (a.hackos_empresa_id && b.hackos_empresa_id && a.hackos_empresa_id === b.hackos_empresa_id) return true;
  const na = normalizarNomeDeEmpresa(a.nome);
  return na.length >= 3 && na === normalizarNomeDeEmpresa(b.nome);
}

export type Conflito =
  | { tipo: 'CARTEIRA'; atendida: string; concorrente: string }
  | { tipo: 'VINCULO_DECLARADO'; declarado: string; relacao: RelacaoDeclarada; concorrente: string };

export type EmpresaComConcorrentes = Identidade & { concorrentes: Identidade[] };

export function vinculoDeclaradoVale(ate: Date | null, agora: Date): boolean {
  if (!ate) return true;
  const limite = new Date(agora);
  limite.setUTCFullYear(limite.getUTCFullYear() - ANOS_DO_VINCULO_DECLARADO);
  return ate.getTime() >= limite.getTime();
}

export function conflitosDoVinculo(f: {
  empresa: EmpresaComConcorrentes;
  /** As outras empresas que o conselheiro atende agora (qualquer papel). */
  carteira: EmpresaComConcorrentes[];
  declarados: (Identidade & { relacao: RelacaoDeclarada; ate: Date | null })[];
  agora?: Date;
}): Conflito[] {
  const agora = f.agora ?? new Date();
  const conflitos: Conflito[] = [];
  for (const atendida of f.carteira) {
    if (atendida.concorrentes.some((c) => mesmaEmpresa(c, f.empresa))) {
      conflitos.push({ tipo: 'CARTEIRA', atendida: atendida.nome, concorrente: f.empresa.nome });
    } else if (f.empresa.concorrentes.some((c) => mesmaEmpresa(c, atendida))) {
      conflitos.push({ tipo: 'CARTEIRA', atendida: atendida.nome, concorrente: atendida.nome });
    }
  }
  for (const d of f.declarados) {
    if (!vinculoDeclaradoVale(d.ate, agora)) continue;
    const c = f.empresa.concorrentes.find((x) => mesmaEmpresa(x, d));
    if (c) conflitos.push({ tipo: 'VINCULO_DECLARADO', declarado: d.nome, relacao: d.relacao, concorrente: c.nome });
  }
  return conflitos;
}

export function textoDoConflito(c: Conflito, empresa: string): string {
  if (c.tipo === 'CARTEIRA') {
    return c.concorrente === empresa
      ? `Já atende ${c.atendida}, que lista ${empresa} como concorrente.`
      : `Já atende ${c.atendida}, que ${empresa} lista como concorrente.`;
  }
  return `Declarou ser ou ter sido ${NOME_DA_RELACAO[c.relacao]} de ${c.declarado}, concorrente de ${empresa}.`;
}
